import { useModal } from "../../../../hooks/useModal";
import { Modal } from "../../../../shared/ui/modal";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Therapist } from "../../../therapist";
import { updateTherapistProfile } from "../../application/therapistProfile";
import { useTherapist } from "../../../../context/useTherapist";
import { countryName, sortedCountries } from "../../../../shared/domain/countries";


interface UserInfoCardProps {
  therapist: Therapist | null; // Allow null as per current usage
  onUpdated?: (t: Therapist) => void;
}

export default function UserInfoCard({ therapist, onUpdated }: UserInfoCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language;
  const countryOptions = useMemo(() => sortedCountries(language), [language]);
  const { isOpen, openModal, closeModal } = useModal();

  const { setTherapist } = useTherapist();

  const [form, setForm] = useState({
    first_name: therapist?.first_name ?? "",
    last_name: therapist?.last_name ?? "",
    email: therapist?.email ?? "",
    country: therapist?.country ?? "ES",
  });

  useEffect(() => {
    setForm({
      first_name: therapist?.first_name ?? "",
      last_name: therapist?.last_name ?? "",
      email: therapist?.email ?? "",
      country: therapist?.country ?? "ES",
    });
  }, [therapist, isOpen]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!therapist) return;
    try {
      const updated = await updateTherapistProfile(therapist.id, form);

      setTherapist(updated);
      onUpdated?.(updated);

      closeModal();
    } catch (err) {
      console.error("Error updating therapist", err);
    }
  };

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h4 className="text-xl font-bold text-content-primary">
            {t("profile.personalInfo")}
          </h4>
          <p className="text-sm text-content-tertiary">
            {t("profile.editInfoDesc")}
          </p>
        </div>

        <Button
          size="sm"
          variant="outline"
          className="text-content-tertiary hover:text-brand-500 hover:bg-brand-50"
          onClick={openModal}
        >
          {t("common.edit")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
        {[
          { label: t("profile.firstName"), value: therapist?.first_name },
          { label: t("profile.lastName"), value: therapist?.last_name },
          { label: t("profile.email"), value: therapist?.email },
          { label: t("auth.country"), value: therapist?.country ? countryName(therapist.country, language) : undefined },
        ].map((item, idx) => (
          <div key={idx} className="space-y-1.5 group">
            <p className="text-xs font-bold uppercase tracking-wider text-content-tertiary transition-colors group-hover:text-brand-500">
              {item.label}
            </p>
            <p className="text-content-primary font-medium">
              {item.value || "—"}
            </p>
          </div>
        ))}
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[700px] m-4">
        <div className="no-scrollbar relative w-full overflow-y-auto rounded-3xl bg-surface-raised p-8 lg:p-12 shadow-2xl border border-gray-200/50 dark:border-white/5">
          <div className="space-y-2 mb-8">
            <h4 className="text-3xl font-extrabold text-content-primary">
              {t("profile.editInfoTitle")}
            </h4>
            <p className="text-content-tertiary">
              {t("profile.editInfoDesc")}
            </p>
          </div>

          <form className="space-y-8" onSubmit={handleSave}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-content-secondary ml-1">{t("profile.firstName")}</Label>
                  <Input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    required
                    className="rounded-xl border-gray-200 dark:border-white/10 dark:bg-white/5 focus:ring-brand-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-content-secondary ml-1">{t("profile.lastName")}</Label>
                  <Input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    required
                    className="rounded-xl border-gray-200 dark:border-white/10 dark:bg-white/5 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-bold text-content-secondary ml-1">{t("auth.country")}</Label>
                <div className="relative">
                  <select
                    className="field-base field-default appearance-none px-4 py-3 focus:border-accent"
                    value={form.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                  >
                    {countryOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-content-tertiary">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-white/5">
              <Button size="md" variant="outline" onClick={closeModal} className="rounded-xl px-8">
                {t("common.close")}
              </Button>
              <Button type="submit" size="md" className="rounded-xl px-12 font-bold shadow-lg shadow-brand-500/20">
                {t("common.saveChanges")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
